import {
  type CellFormatOverride,
  getCellValueInCurrentColumn,
} from "@platform/plan-grid-core";
import {
  COLLAPSED_SIBLINGS_KEY,
  formatAccidentField,
  formatBasicLifeField,
  formatCancerField,
  formatCriticalIllnessField,
  formatDentalHmoField,
  formatDentalPpoField,
  formatEapField,
  formatFmlField,
  formatHospitalField,
  formatIdiField,
  formatLtdField,
  formatStdField,
  formatVisionField,
  formatVolLifeField,
  formatWholeLifeField,
  FUNDING_TYPE_KEY,
} from "@platform/formatting-utils";
import { CoverageTypeEnum } from "@platform/types";

/**
 * Map of CoverageTypeEnum → per-field formatter for the Quotes tab. Each
 * formatter receives a v5-shaped benefit row and returns the cell display
 * string (or undefined to fall through to the default rendering).
 *
 * Add new coverages here as their FIELD_RULES.md formatters land.
 */
const FORMATTERS_BY_COVERAGE: Partial<
  Record<
    CoverageTypeEnum,
    (fieldId: string, row: Record<string, unknown>) => string | undefined
  >
> = {
  [CoverageTypeEnum.BASIC_LIFE_ADND]: formatBasicLifeField,
  [CoverageTypeEnum.CANCER]: formatCancerField,
  [CoverageTypeEnum.ACCIDENT]: formatAccidentField,
  [CoverageTypeEnum.HOSPITAL_INDEMNITY]: formatHospitalField,
  [CoverageTypeEnum.CRITICAL_ILLNESS]: formatCriticalIllnessField,
  [CoverageTypeEnum.DENTAL_HMO]: formatDentalHmoField,
  [CoverageTypeEnum.DENTAL_PPO]: formatDentalPpoField,
  [CoverageTypeEnum.LTD]: formatLtdField,
  [CoverageTypeEnum.STD]: formatStdField,
  [CoverageTypeEnum.VISION]: formatVisionField,
  [CoverageTypeEnum.VOLUNTARY_LIFE_ADND]: formatVolLifeField,
  [CoverageTypeEnum.EAP]: formatEapField,
  [CoverageTypeEnum.FMLA]: formatFmlField,
  [CoverageTypeEnum.INDIVIDUAL_DISABILITY_INSURANCE]: formatIdiField,
  [CoverageTypeEnum.WHOLE_LIFE]: formatWholeLifeField,
};

/**
 * Builds a `CellFormatOverride` for the Quotes tab. The override applies
 * to every cell in the grid (requested, incumbent, and proposals) — it is
 * a view-only display concern. Editability is governed separately.
 *
 * Returns `null` when the selected coverage has no Quotes-specific
 * formatter, so the grid falls back to the canonical per-LOC formatters
 * inside `@platform/btx-plan-designer`.
 */
export const buildQuotesCellFormatOverride = (
  selectedCoverageType: CoverageTypeEnum | undefined,
): CellFormatOverride | null => {
  if (!selectedCoverageType) return null;
  const format = FORMATTERS_BY_COVERAGE[selectedCoverageType];

  // Always return a formatter so fundingType gets a "-" fallback even
  // for coverages with no coverage-specific formatter.
  return (params) => {
    const data = params.data as
      | {
          benefitKey?: string;
          [COLLAPSED_SIBLINGS_KEY]?: Record<string, Record<string, unknown>>;
        }
      | undefined;
    const benefitKey = data?.benefitKey;
    if (!benefitKey) return undefined;

    // Show "-" for fundingType when the cell has no value.
    if (
      benefitKey === FUNDING_TYPE_KEY &&
      (params.value == null || params.value === "")
    )
      return "-";

    if (!format) return undefined;

    const columnField = params.colDef?.field;
    const collapsedSiblings = data?.[COLLAPSED_SIBLINGS_KEY];

    // Lazily resolve sibling field values from the current column so the
    // formatter sees a v5-shaped row without us pre-materializing every
    // benefit on every render. Sibling rows that were collapsed onto
    // this row by `transformRowData` (e.g. cancer's `*Amount` fields)
    // are stashed under `__collapsedSiblings`; consult that first
    // before falling back to a grid-API lookup, since those rows no
    // longer exist in the row model.
    const row = new Proxy(
      {},
      {
        get: (_target, prop) => {
          if (typeof prop !== "string") return undefined;
          if (prop === benefitKey) return params.value as unknown;
          if (collapsedSiblings && prop in collapsedSiblings && columnField) {
            const byColumn = collapsedSiblings[prop];
            // Try the exact column, then fall back to the bare
            // plan id so non-network typed-amount fields (e.g. Rate Guarantee)
            // still display after a grid rebuild.
            if (columnField in byColumn) return byColumn[columnField];
            return byColumn[columnField.split(".")[0]];
          }
          return getCellValueInCurrentColumn(params, prop) as unknown;
        },
      },
    ) as Record<string, unknown>;

    // The grid emits one row per type-discriminator field (e.g.
    // `hospiceType`), but the cancer formatter registry is keyed by
    // the parent benefit (`hospice`). Try the row key directly first,
    // then strip a trailing `Type` and try again.
    const direct = format(benefitKey, row);
    if (direct !== undefined) return direct;
    if (benefitKey.endsWith("Type")) {
      return format(benefitKey.slice(0, -"Type".length), row);
    }
    return undefined;
  };
};
